/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useState, useEffect, useCallback } from "react";
import {
  Layout,
  Typography,
  Card,
  Row,
  Col,
  Button,
  Input,
  Select,
  Space,
  Table,
  Tag,
  Badge,
  Statistic,
  Spin,
  message,
  Modal,
  List,
  Divider,
  Progress,
} from "antd";
import {
  NodeIndexOutlined,
  BranchesOutlined,
  SearchOutlined,
  EyeOutlined,
  EditOutlined,
  DeleteOutlined,
  ReloadOutlined,
  WarningOutlined,
  CheckCircleOutlined,
  BulbOutlined,
  ExperimentOutlined,
  EnvironmentOutlined,
  ThunderboltOutlined,
  CloudOutlined,
  SyncOutlined,
  BarChartOutlined,
} from "@ant-design/icons";
import { useAuth } from "../contexts/AuthContext";

const { Content } = Layout;
const { Title, Text, Paragraph } = Typography;
const { Option } = Select;
const { Search } = Input;

interface GraphNode {
  id: string;
  type:
    | "soil"
    | "energy"
    | "weather"
    | "crop"
    | "field"
    | "device"
    | "user"
    | "event";
  label: string;
  properties: Record<string, any>;
  position: { x: number; y: number };
  connections: string[];
  createdAt: string;
  updatedAt: string;
}

interface GraphEdge {
  id: string;
  source: string;
  target: string;
  type:
    | "affects"
    | "correlates"
    | "measured_at"
    | "located_in"
    | "generates"
    | "consumes";
  properties: Record<string, any>;
  weight: number;
  createdAt: string;
}

interface GraphPattern {
  id: string;
  name: string;
  description: string;
  nodes: string[];
  edges: string[];
  confidence: number;
  type: "correlation" | "anomaly" | "prediction" | "optimization";
  createdAt: string;
}

interface GNNAlgorithm {
  id: string;
  name: string;
  type:
    | "node_classification"
    | "link_prediction"
    | "graph_classification"
    | "clustering"
    | "recommendation";
  description: string;
  accuracy: number;
  trainingStatus: "idle" | "training" | "completed" | "failed";
  lastTrained: string | null;
  parameters: Record<string, any>;
  performance: {
    precision: number;
    recall: number;
    f1Score: number;
    auc: number;
  };
}

interface DataScienceTask {
  id: string;
  name: string;
  type:
    | "prediction"
    | "classification"
    | "clustering"
    | "regression"
    | "anomaly_detection";
  status: "pending" | "running" | "completed" | "failed";
  algorithm: string;
  dataset: string;
  progress: number;
  results: Record<string, any>;
  createdAt: string;
  completedAt: string | null;
}

const KnowledgeGraphPage: React.FC = () => {
  const [nodes, setNodes] = useState<GraphNode[]>([]);
  const [edges, setEdges] = useState<GraphEdge[]>([]);
  const [patterns, setPatterns] = useState<GraphPattern[]>([]);
  const [gnnAlgorithms, setGnnAlgorithms] = useState<GNNAlgorithm[]>([]);
  const [dataScienceTasks, setDataScienceTasks] = useState<DataScienceTask[]>(
    [],
  );
  const [loading, setLoading] = useState(true);
  const [selectedNode, setSelectedNode] = useState<GraphNode | null>(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [filterType, setFilterType] = useState<string>("all");
  const [activeTab, setActiveTab] = useState<"graph" | "algorithms" | "tasks">(
    "graph",
  );

  // Mock data for development
  const mockNodes: GraphNode[] = [
    {
      id: "soil-1",
      type: "soil",
      label: "North Field Soil",
      properties: {
        ph: 6.5,
        nitrogen: 45,
        phosphorus: 35,
        organicMatter: 3.2,
        moisture: 25,
        temperature: 18,
      },
      position: { x: 100, y: 100 },
      connections: ["field-1", "weather-1"],
      createdAt: "2024-01-15T10:00:00Z",
      updatedAt: "2024-01-15T10:00:00Z",
    },
    {
      id: "energy-1",
      type: "energy",
      label: "Solar Array Alpha",
      properties: {
        powerOutput: 42.5,
        efficiency: 85,
        voltage: 240,
        temperature: 45,
      },
      position: { x: 300, y: 100 },
      connections: ["device-1", "field-1"],
      createdAt: "2024-01-15T10:00:00Z",
      updatedAt: "2024-01-15T10:00:00Z",
    },
    {
      id: "weather-1",
      type: "weather",
      label: "Weather Station",
      properties: {
        temperature: 22,
        humidity: 65,
        windSpeed: 12,
        solarIrradiance: 850,
      },
      position: { x: 200, y: 200 },
      connections: ["soil-1", "energy-1"],
      createdAt: "2024-01-15T10:00:00Z",
      updatedAt: "2024-01-15T10:00:00Z",
    },
    {
      id: "field-1",
      type: "field",
      label: "North Field",
      properties: {
        size: 25,
        cropType: "Wheat",
        location: "40.7128, -74.0060",
        healthScore: 85,
      },
      position: { x: 100, y: 300 },
      connections: ["soil-1", "energy-1"],
      createdAt: "2024-01-15T10:00:00Z",
      updatedAt: "2024-01-15T10:00:00Z",
    },
    {
      id: "device-1",
      type: "device",
      label: "IoT Sensor Network",
      properties: {
        sensorCount: 12,
        batteryLevel: 85,
        lastCalibration: "2024-01-10",
        status: "online",
      },
      position: { x: 300, y: 300 },
      connections: ["energy-1", "field-1"],
      createdAt: "2024-01-15T10:00:00Z",
      updatedAt: "2024-01-15T10:00:00Z",
    },
  ];

  const mockEdges: GraphEdge[] = [
    {
      id: "edge-1",
      source: "soil-1",
      target: "field-1",
      type: "located_in",
      properties: { depth: 15, sampleDate: "2024-01-15" },
      weight: 1.0,
      createdAt: "2024-01-15T10:00:00Z",
    },
    {
      id: "edge-2",
      source: "energy-1",
      target: "field-1",
      type: "generates",
      properties: { dailyOutput: 1020, efficiency: 85 },
      weight: 0.9,
      createdAt: "2024-01-15T10:00:00Z",
    },
    {
      id: "edge-3",
      source: "weather-1",
      target: "soil-1",
      type: "affects",
      properties: { impact: "moisture", correlation: 0.8 },
      weight: 0.8,
      createdAt: "2024-01-15T10:00:00Z",
    },
    {
      id: "edge-4",
      source: "weather-1",
      target: "energy-1",
      type: "affects",
      properties: { impact: "efficiency", correlation: 0.7 },
      weight: 0.7,
      createdAt: "2024-01-15T10:00:00Z",
    },
    {
      id: "edge-5",
      source: "device-1",
      target: "energy-1",
      type: "measured_at",
      properties: { frequency: "5min", accuracy: 99.5 },
      weight: 0.95,
      createdAt: "2024-01-15T10:00:00Z",
    },
  ];

  const mockPatterns: GraphPattern[] = [
    {
      id: "pattern-1",
      name: "Weather-Energy Correlation",
      description:
        "Strong correlation between solar irradiance and energy output",
      nodes: ["weather-1", "energy-1"],
      edges: ["edge-4"],
      confidence: 0.87,
      type: "correlation",
      createdAt: "2024-01-15T10:00:00Z",
    },
    {
      id: "pattern-2",
      name: "Soil Moisture Anomaly",
      description: "Unusual soil moisture levels detected in North Field",
      nodes: ["soil-1", "weather-1"],
      edges: ["edge-3"],
      confidence: 0.92,
      type: "anomaly",
      createdAt: "2024-01-15T10:00:00Z",
    },
    {
      id: "pattern-3",
      name: "Energy Optimization",
      description: "Potential for 15% energy efficiency improvement",
      nodes: ["energy-1", "device-1"],
      edges: ["edge-5"],
      confidence: 0.78,
      type: "optimization",
      createdAt: "2024-01-15T10:00:00Z",
    },
  ];

  const mockGnnAlgorithms: GNNAlgorithm[] = [
    {
      id: "gnn-1",
      name: "GraphSAGE",
      type: "node_classification",
      description: "Inductive representation learning on large graphs",
      accuracy: 94.2,
      trainingStatus: "completed",
      lastTrained: "2024-01-15T10:00:00Z",
      parameters: {
        hiddenSize: 256,
        numLayers: 3,
        learningRate: 0.001,
        dropout: 0.2,
      },
      performance: {
        precision: 0.92,
        recall: 0.94,
        f1Score: 0.93,
        auc: 0.96,
      },
    },
    {
      id: "gnn-2",
      name: "GAT (Graph Attention Network)",
      type: "link_prediction",
      description: "Attention-based graph neural networks",
      accuracy: 91.8,
      trainingStatus: "completed",
      lastTrained: "2024-01-14T15:30:00Z",
      parameters: {
        numHeads: 8,
        hiddenSize: 128,
        numLayers: 2,
        attentionDropout: 0.1,
      },
      performance: {
        precision: 0.89,
        recall: 0.92,
        f1Score: 0.9,
        auc: 0.94,
      },
    },
    {
      id: "gnn-3",
      name: "GCN (Graph Convolutional Network)",
      type: "graph_classification",
      description: "Semi-supervised classification with graph convolutions",
      accuracy: 88.5,
      trainingStatus: "training",
      lastTrained: null,
      parameters: {
        hiddenSize: 64,
        numLayers: 2,
        learningRate: 0.01,
        weightDecay: 0.0005,
      },
      performance: {
        precision: 0.85,
        recall: 0.88,
        f1Score: 0.86,
        auc: 0.91,
      },
    },
    {
      id: "gnn-4",
      name: "Node2Vec",
      type: "clustering",
      description: "Scalable feature learning for networks",
      accuracy: 86.3,
      trainingStatus: "idle",
      lastTrained: "2024-01-13T09:15:00Z",
      parameters: {
        embeddingSize: 128,
        walkLength: 80,
        numWalks: 10,
        p: 1,
        q: 1,
      },
      performance: {
        precision: 0.83,
        recall: 0.86,
        f1Score: 0.84,
        auc: 0.89,
      },
    },
  ];

  const mockDataScienceTasks: DataScienceTask[] = [
    {
      id: "task-1",
      name: "Energy Output Prediction",
      type: "prediction",
      status: "completed",
      algorithm: "GraphSAGE + LSTM",
      dataset: "Energy_Weather_Graph",
      progress: 100,
      results: {
        mse: 0.023,
        mae: 0.156,
        r2: 0.94,
        predictions: 1500,
      },
      createdAt: "2024-01-15T08:00:00Z",
      completedAt: "2024-01-15T10:30:00Z",
    },
    {
      id: "task-2",
      name: "Soil Health Classification",
      type: "classification",
      status: "running",
      algorithm: "GAT + Random Forest",
      dataset: "Soil_Sensor_Network",
      progress: 65,
      results: {},
      createdAt: "2024-01-15T11:00:00Z",
      completedAt: null,
    },
    {
      id: "task-3",
      name: "Anomaly Detection",
      type: "anomaly_detection",
      status: "completed",
      algorithm: "GCN + Isolation Forest",
      dataset: "IoT_Sensor_Data",
      progress: 100,
      results: {
        anomalies: 23,
        precision: 0.91,
        recall: 0.87,
        f1Score: 0.89,
      },
      createdAt: "2024-01-14T14:00:00Z",
      completedAt: "2024-01-14T16:45:00Z",
    },
    {
      id: "task-4",
      name: "Crop Yield Regression",
      type: "regression",
      status: "pending",
      algorithm: "Node2Vec + XGBoost",
      dataset: "Agricultural_Graph",
      progress: 0,
      results: {},
      createdAt: "2024-01-15T12:00:00Z",
      completedAt: null,
    },
  ];

  const loadData = useCallback(async () => {
    try {
      // In production, fetch from API
      setNodes(mockNodes);
      setEdges(mockEdges);
      setPatterns(mockPatterns);
      setGnnAlgorithms(mockGnnAlgorithms);
      setDataScienceTasks(mockDataScienceTasks);
    } catch (error) {
      message.error("Failed to load knowledge graph data");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const getNodeIcon = (type: string) => {
    switch (type) {
      case "soil":
        return <EnvironmentOutlined />;
      case "energy":
        return <ThunderboltOutlined />;
      case "weather":
        return <CloudOutlined />;
      case "crop":
        return <ExperimentOutlined />;
      case "field":
        return <EnvironmentOutlined />;
      case "device":
        return <BulbOutlined />;
      case "user":
        return <CheckCircleOutlined />;
      case "event":
        return <WarningOutlined />;
      default:
        return <NodeIndexOutlined />;
    }
  };

  const getNodeColor = (type: string) => {
    switch (type) {
      case "soil":
        return "#52c41a";
      case "energy":
        return "#1890ff";
      case "weather":
        return "#722ed1";
      case "crop":
        return "#faad14";
      case "field":
        return "#13c2c2";
      case "device":
        return "#eb2f96";
      case "user":
        return "#f5222d";
      case "event":
        return "#fa8c16";
      default:
        return "#666";
    }
  };

  const getEdgeColor = (type: string) => {
    switch (type) {
      case "affects":
        return "#f5222d";
      case "correlates":
        return "#1890ff";
      case "measured_at":
        return "#52c41a";
      case "located_in":
        return "#722ed1";
      case "generates":
        return "#faad14";
      case "consumes":
        return "#eb2f96";
      default:
        return "#666";
    }
  };

  const filteredNodes = nodes.filter((node) => {
    const matchesSearch =
      node.label.toLowerCase().includes(searchQuery.toLowerCase()) ||
      Object.values(node.properties).some((val) =>
        val?.toString().toLowerCase().includes(searchQuery.toLowerCase()),
      );
    const matchesType = filterType === "all" || node.type === filterType;
    return matchesSearch && matchesType;
  });

  const nodeColumns = [
    {
      title: "Node",
      dataIndex: "label",
      key: "label",
      render: (label: string, record: GraphNode) => (
        <Space>
          <div
            style={{
              color: getNodeColor(record.type),
              fontSize: "16px",
            }}
          >
            {getNodeIcon(record.type)}
          </div>
          <div>
            <Text strong>{label}</Text>
            <br />
            <Tag color={getNodeColor(record.type)}>{record.type}</Tag>
          </div>
        </Space>
      ),
    },
    {
      title: "Properties",
      key: "properties",
      render: (record: GraphNode) => (
        <div>
          {Object.entries(record.properties)
            .slice(0, 3)
            .map(([key, value]) => (
              <div key={key}>
                <Text type="secondary">{key}:</Text> {value}
              </div>
            ))}
          {Object.keys(record.properties).length > 3 && (
            <Text type="secondary">
              +{Object.keys(record.properties).length - 3} more
            </Text>
          )}
        </div>
      ),
    },
    {
      title: "Connections",
      dataIndex: "connections",
      key: "connections",
      render: (connections: string[]) => (
        <Text>{connections.length} connections</Text>
      ),
    },
    {
      title: "Actions",
      key: "actions",
      render: (record: GraphNode) => (
        <Space>
          <Button
            type="link"
            icon={<EyeOutlined />}
            size="small"
            onClick={() => setSelectedNode(record)}
          >
            View
          </Button>
          <Button type="link" icon={<EditOutlined />} size="small">
            Edit
          </Button>
          <Button type="link" danger icon={<DeleteOutlined />} size="small">
            Delete
          </Button>
        </Space>
      ),
    },
  ];

  const patternColumns = [
    {
      title: "Pattern",
      key: "pattern",
      render: (record: GraphPattern) => (
        <div>
          <Text strong>{record.name}</Text>
          <br />
          <Text type="secondary">{record.description}</Text>
        </div>
      ),
    },
    {
      title: "Type",
      dataIndex: "type",
      key: "type",
      render: (type: string) => (
        <Tag
          color={
            type === "correlation"
              ? "blue"
              : type === "anomaly"
                ? "red"
                : type === "prediction"
                  ? "green"
                  : "orange"
          }
        >
          {type.charAt(0).toUpperCase() + type.slice(1)}
        </Tag>
      ),
    },
    {
      title: "Confidence",
      dataIndex: "confidence",
      key: "confidence",
      render: (confidence: number) => (
        <Text
          strong
          style={{
            color:
              confidence >= 0.8
                ? "#52c41a"
                : confidence >= 0.6
                  ? "#faad14"
                  : "#f5222d",
          }}
        >
          {(confidence * 100).toFixed(1)}%
        </Text>
      ),
    },
    {
      title: "Nodes",
      dataIndex: "nodes",
      key: "nodes",
      render: (nodes: string[]) => <Text>{nodes.length} nodes</Text>,
    },
  ];

  const totalNodes = nodes.length;
  const totalEdges = edges.length;
  const totalPatterns = patterns.length;
  const averageConfidence =
    patterns.reduce((sum, p) => sum + p.confidence, 0) / patterns.length;

  if (loading) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "60vh",
        }}
      >
        <Spin size="large" />
      </div>
    );
  }

  return (
    <Layout style={{ minHeight: "100vh", background: "#f0f2f5" }}>
      <Content style={{ padding: "24px" }}>
        <div style={{ maxWidth: "1400px", margin: "0 auto" }}>
          {/* Header */}
          <div style={{ marginBottom: "32px" }}>
            <Title level={1} style={{ marginBottom: "8px" }}>
              <NodeIndexOutlined style={{ marginRight: "12px" }} />
              Knowledge Graph
            </Title>
            <Paragraph style={{ fontSize: "16px", color: "#666" }}>
              Explore relationships, discover patterns, and unlock insights
              across your Expedient platform
            </Paragraph>
          </div>

          {/* Summary Statistics */}
          <Row gutter={[16, 16]} style={{ marginBottom: "32px" }}>
            <Col xs={24} sm={12} md={6}>
              <Card>
                <Statistic
                  title="Total Nodes"
                  value={totalNodes}
                  prefix={<NodeIndexOutlined />}
                  valueStyle={{ color: "#1890ff" }}
                />
              </Card>
            </Col>
            <Col xs={24} sm={12} md={6}>
              <Card>
                <Statistic
                  title="Total Edges"
                  value={totalEdges}
                  prefix={<BranchesOutlined />}
                  valueStyle={{ color: "#52c41a" }}
                />
              </Card>
            </Col>
            <Col xs={24} sm={12} md={6}>
              <Card>
                <Statistic
                  title="Discovered Patterns"
                  value={totalPatterns}
                  prefix={<BulbOutlined />}
                  valueStyle={{ color: "#722ed1" }}
                />
              </Card>
            </Col>
            <Col xs={24} sm={12} md={6}>
              <Card>
                <Statistic
                  title="Avg Confidence"
                  value={averageConfidence}
                  suffix="%"
                  prefix={<CheckCircleOutlined />}
                  valueStyle={{ color: "#faad14" }}
                />
              </Card>
            </Col>
          </Row>

          {/* Search and Filters */}
          <Card style={{ marginBottom: "32px" }}>
            <Row gutter={[16, 16]} align="middle">
              <Col xs={24} md={12}>
                <Search
                  placeholder="Search nodes, properties, or relationships..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  prefix={<SearchOutlined />}
                  size="large"
                />
              </Col>
              <Col xs={24} md={8}>
                <Select
                  value={filterType}
                  onChange={setFilterType}
                  style={{ width: "100%" }}
                  size="large"
                >
                  <Option value="all">All Types</Option>
                  <Option value="soil">Soil</Option>
                  <Option value="energy">Energy</Option>
                  <Option value="weather">Weather</Option>
                  <Option value="crop">Crop</Option>
                  <Option value="field">Field</Option>
                  <Option value="device">Device</Option>
                  <Option value="user">User</Option>
                  <Option value="event">Event</Option>
                </Select>
              </Col>
              <Col xs={24} md={4}>
                <Button
                  icon={<ReloadOutlined />}
                  onClick={loadData}
                  size="large"
                  style={{ width: "100%" }}
                >
                  Refresh
                </Button>
              </Col>
            </Row>
          </Card>

          {/* Tab Navigation */}
          <Card style={{ marginBottom: "32px" }}>
            <Row gutter={[16, 16]}>
              <Col>
                <Button
                  type={activeTab === "graph" ? "primary" : "default"}
                  onClick={() => setActiveTab("graph")}
                  icon={<NodeIndexOutlined />}
                  size="large"
                >
                  Knowledge Graph
                </Button>
              </Col>
              <Col>
                <Button
                  type={activeTab === "algorithms" ? "primary" : "default"}
                  onClick={() => setActiveTab("algorithms")}
                  icon={<ExperimentOutlined />}
                  size="large"
                >
                  GNN Algorithms
                </Button>
              </Col>
              <Col>
                <Button
                  type={activeTab === "tasks" ? "primary" : "default"}
                  onClick={() => setActiveTab("tasks")}
                  icon={<BulbOutlined />}
                  size="large"
                >
                  Data Science Tasks
                </Button>
              </Col>
            </Row>
          </Card>

          {/* Graph Tab Content */}
          {activeTab === "graph" && (
            <>
              {/* Graph Visualization Placeholder */}
              <Card
                title="Graph Visualization"
                style={{ marginBottom: "32px", height: "400px" }}
              >
                <div
                  style={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    height: "300px",
                    background: "#fafafa",
                    border: "2px dashed #d9d9d9",
                    borderRadius: "8px",
                  }}
                >
                  <div style={{ textAlign: "center" }}>
                    <NodeIndexOutlined
                      style={{
                        fontSize: "48px",
                        color: "#d9d9d9",
                        marginBottom: "16px",
                      }}
                    />
                    <Text type="secondary">
                      Interactive graph visualization coming soon
                    </Text>
                    <br />
                    <Text type="secondary">
                      Powered by D3.js and Graph Neural Networks
                    </Text>
                  </div>
                </div>
              </Card>

              {/* Patterns */}
              <Card
                title="AI-Discovered Patterns"
                style={{ marginBottom: "32px" }}
              >
                <Table
                  columns={patternColumns}
                  dataSource={patterns}
                  rowKey="id"
                  pagination={{ pageSize: 5 }}
                />
              </Card>

              {/* Nodes Table */}
              <Card title="Knowledge Graph Nodes">
                <Table
                  columns={nodeColumns}
                  dataSource={filteredNodes}
                  rowKey="id"
                  pagination={{ pageSize: 10 }}
                />
              </Card>
            </>
          )}

          {/* GNN Algorithms Tab */}
          {activeTab === "algorithms" && (
            <div style={{ maxWidth: "1400px", margin: "0 auto" }}>
              {/* GNN Algorithms Overview */}
              <Card
                title="Graph Neural Network Algorithms"
                style={{ marginBottom: "32px" }}
              >
                <Row gutter={[16, 16]}>
                  <Col xs={24} sm={12} md={6}>
                    <Statistic
                      title="Total Algorithms"
                      value={gnnAlgorithms.length}
                      prefix={<ExperimentOutlined />}
                    />
                  </Col>
                  <Col xs={24} sm={12} md={6}>
                    <Statistic
                      title="Training"
                      value={
                        gnnAlgorithms.filter(
                          (a) => a.trainingStatus === "training",
                        ).length
                      }
                      prefix={<SyncOutlined spin />}
                      valueStyle={{ color: "#1890ff" }}
                    />
                  </Col>
                  <Col xs={24} sm={12} md={6}>
                    <Statistic
                      title="Completed"
                      value={
                        gnnAlgorithms.filter(
                          (a) => a.trainingStatus === "completed",
                        ).length
                      }
                      prefix={<CheckCircleOutlined />}
                      valueStyle={{ color: "#52c41a" }}
                    />
                  </Col>
                  <Col xs={24} sm={12} md={6}>
                    <Statistic
                      title="Avg Accuracy"
                      value={Math.round(
                        gnnAlgorithms.reduce((sum, a) => sum + a.accuracy, 0) /
                          gnnAlgorithms.length,
                      )}
                      suffix="%"
                      prefix={<BarChartOutlined />}
                    />
                  </Col>
                </Row>
              </Card>

              {/* GNN Algorithms Table */}
              <Card title="Available GNN Algorithms">
                <Table
                  columns={[
                    {
                      title: "Algorithm",
                      key: "algorithm",
                      render: (record: GNNAlgorithm) => (
                        <div>
                          <Text strong>{record.name}</Text>
                          <br />
                          <Tag color="blue">
                            {record.type.replace("_", " ").toUpperCase()}
                          </Tag>
                          <br />
                          <Text type="secondary">{record.description}</Text>
                        </div>
                      ),
                    },
                    {
                      title: "Status",
                      key: "status",
                      render: (record: GNNAlgorithm) => (
                        <Badge
                          status={
                            record.trainingStatus === "completed"
                              ? "success"
                              : record.trainingStatus === "training"
                                ? "processing"
                                : record.trainingStatus === "failed"
                                  ? "error"
                                  : "default"
                          }
                          text={
                            record.trainingStatus.charAt(0).toUpperCase() +
                            record.trainingStatus.slice(1)
                          }
                        />
                      ),
                    },
                    {
                      title: "Accuracy",
                      dataIndex: "accuracy",
                      key: "accuracy",
                      render: (accuracy: number) => (
                        <Text
                          strong
                          style={{
                            color:
                              accuracy >= 90
                                ? "#52c41a"
                                : accuracy >= 80
                                  ? "#faad14"
                                  : "#f5222d",
                          }}
                        >
                          {accuracy}%
                        </Text>
                      ),
                    },
                    {
                      title: "Performance",
                      key: "performance",
                      render: (record: GNNAlgorithm) => (
                        <div>
                          <Text type="secondary">
                            F1: {record.performance.f1Score.toFixed(3)}
                          </Text>
                          <br />
                          <Text type="secondary">
                            AUC: {record.performance.auc.toFixed(3)}
                          </Text>
                        </div>
                      ),
                    },
                    {
                      title: "Actions",
                      key: "actions",
                      render: (record: GNNAlgorithm) => (
                        <Space>
                          <Button type="link" size="small">
                            Train
                          </Button>
                          <Button type="link" size="small">
                            Evaluate
                          </Button>
                          <Button type="link" size="small">
                            Deploy
                          </Button>
                        </Space>
                      ),
                    },
                  ]}
                  dataSource={gnnAlgorithms}
                  rowKey="id"
                  pagination={{ pageSize: 10 }}
                />
              </Card>
            </div>
          )}

          {/* Data Science Tasks Tab */}
          {activeTab === "tasks" && (
            <div style={{ maxWidth: "1400px", margin: "0 auto" }}>
              {/* Tasks Overview */}
              <Card title="Data Science Tasks" style={{ marginBottom: "32px" }}>
                <Row gutter={[16, 16]}>
                  <Col xs={24} sm={12} md={6}>
                    <Statistic
                      title="Total Tasks"
                      value={dataScienceTasks.length}
                      prefix={<BulbOutlined />}
                    />
                  </Col>
                  <Col xs={24} sm={12} md={6}>
                    <Statistic
                      title="Running"
                      value={
                        dataScienceTasks.filter((t) => t.status === "running")
                          .length
                      }
                      prefix={<SyncOutlined spin />}
                      valueStyle={{ color: "#1890ff" }}
                    />
                  </Col>
                  <Col xs={24} sm={12} md={6}>
                    <Statistic
                      title="Completed"
                      value={
                        dataScienceTasks.filter((t) => t.status === "completed")
                          .length
                      }
                      prefix={<CheckCircleOutlined />}
                      valueStyle={{ color: "#52c41a" }}
                    />
                  </Col>
                  <Col xs={24} sm={12} md={6}>
                    <Statistic
                      title="Success Rate"
                      value={Math.round(
                        (dataScienceTasks.filter(
                          (t) => t.status === "completed",
                        ).length /
                          dataScienceTasks.length) *
                          100,
                      )}
                      suffix="%"
                      prefix={<BarChartOutlined />}
                    />
                  </Col>
                </Row>
              </Card>

              {/* Tasks Table */}
              <Card title="Active Data Science Tasks">
                <Table
                  columns={[
                    {
                      title: "Task",
                      key: "task",
                      render: (record: DataScienceTask) => (
                        <div>
                          <Text strong>{record.name}</Text>
                          <br />
                          <Tag
                            color={
                              record.type === "prediction"
                                ? "blue"
                                : record.type === "classification"
                                  ? "green"
                                  : record.type === "clustering"
                                    ? "purple"
                                    : record.type === "regression"
                                      ? "orange"
                                      : "red"
                            }
                          >
                            {record.type.replace("_", " ").toUpperCase()}
                          </Tag>
                          <br />
                          <Text type="secondary">{record.algorithm}</Text>
                        </div>
                      ),
                    },
                    {
                      title: "Status",
                      key: "status",
                      render: (record: DataScienceTask) => (
                        <div>
                          <Badge
                            status={
                              record.status === "completed"
                                ? "success"
                                : record.status === "running"
                                  ? "processing"
                                  : record.status === "failed"
                                    ? "error"
                                    : "default"
                            }
                            text={
                              record.status.charAt(0).toUpperCase() +
                              record.status.slice(1)
                            }
                          />
                          {record.status === "running" && (
                            <Progress
                              percent={record.progress}
                              size="small"
                              style={{ marginTop: "8px" }}
                            />
                          )}
                        </div>
                      ),
                    },
                    {
                      title: "Dataset",
                      dataIndex: "dataset",
                      key: "dataset",
                    },
                    {
                      title: "Results",
                      key: "results",
                      render: (record: DataScienceTask) => (
                        <div>
                          {record.status === "completed" &&
                          Object.keys(record.results).length > 0 ? (
                            Object.entries(record.results)
                              .slice(0, 2)
                              .map(([key, value]) => (
                                <div key={key}>
                                  <Text type="secondary">{key}:</Text> {value}
                                </div>
                              ))
                          ) : (
                            <Text type="secondary">Pending...</Text>
                          )}
                        </div>
                      ),
                    },
                    {
                      title: "Actions",
                      key: "actions",
                      render: (record: DataScienceTask) => (
                        <Space>
                          {record.status === "pending" && (
                            <Button type="link" size="small">
                              Start
                            </Button>
                          )}
                          {record.status === "running" && (
                            <Button type="link" size="small">
                              Stop
                            </Button>
                          )}
                          {record.status === "completed" && (
                            <Button type="link" size="small">
                              View Results
                            </Button>
                          )}
                          <Button type="link" size="small">
                            Details
                          </Button>
                        </Space>
                      ),
                    },
                  ]}
                  dataSource={dataScienceTasks}
                  rowKey="id"
                  pagination={{ pageSize: 10 }}
                />
              </Card>
            </div>
          )}

          {/* Node Detail Modal */}
          <Modal
            title={selectedNode ? `Node: ${selectedNode.label}` : ""}
            open={!!selectedNode}
            onCancel={() => setSelectedNode(null)}
            footer={null}
            width={800}
          >
            {selectedNode && (
              <div>
                <Row gutter={[16, 16]}>
                  <Col span={24}>
                    <div style={{ marginBottom: "16px" }}>
                      <Space>
                        <div
                          style={{
                            color: getNodeColor(selectedNode.type),
                            fontSize: "24px",
                          }}
                        >
                          {getNodeIcon(selectedNode.type)}
                        </div>
                        <div>
                          <Title level={4} style={{ margin: 0 }}>
                            {selectedNode.label}
                          </Title>
                          <Tag color={getNodeColor(selectedNode.type)}>
                            {selectedNode.type}
                          </Tag>
                        </div>
                      </Space>
                    </div>
                  </Col>
                </Row>

                <Divider />

                <Row gutter={[16, 16]}>
                  <Col span={12}>
                    <Title level={5}>Properties</Title>
                    <List
                      size="small"
                      dataSource={Object.entries(selectedNode.properties)}
                      renderItem={([key, value]) => (
                        <List.Item>
                          <Text type="secondary">{key}:</Text> {value}
                        </List.Item>
                      )}
                    />
                  </Col>
                  <Col span={12}>
                    <Title level={5}>Connections</Title>
                    <List
                      size="small"
                      dataSource={selectedNode.connections}
                      renderItem={(connectionId) => {
                        const connectedNode = nodes.find(
                          (n) => n.id === connectionId,
                        );
                        const edge = edges.find(
                          (e) =>
                            (e.source === selectedNode.id &&
                              e.target === connectionId) ||
                            (e.source === connectionId &&
                              e.target === selectedNode.id),
                        );
                        return (
                          <List.Item>
                            <Space>
                              <div
                                style={{
                                  color: getNodeColor(
                                    connectedNode?.type || "",
                                  ),
                                }}
                              >
                                {getNodeIcon(connectedNode?.type || "")}
                              </div>
                              <Text>{connectedNode?.label}</Text>
                              {edge && (
                                <Tag color={getEdgeColor(edge.type)}>
                                  {edge.type}
                                </Tag>
                              )}
                            </Space>
                          </List.Item>
                        );
                      }}
                    />
                  </Col>
                </Row>

                <Divider />

                <Row>
                  <Col span={24}>
                    <Title level={5}>Metadata</Title>
                    <Text type="secondary">
                      Created:{" "}
                      {new Date(selectedNode.createdAt).toLocaleString()}
                      <br />
                      Updated:{" "}
                      {new Date(selectedNode.updatedAt).toLocaleString()}
                      <br />
                      Position: ({selectedNode.position.x},{" "}
                      {selectedNode.position.y})
                    </Text>
                  </Col>
                </Row>
              </div>
            )}
          </Modal>
        </div>
      </Content>
    </Layout>
  );
};

export default KnowledgeGraphPage; 
